import {
  describeCondition,
  formatMetricValue,
  type MetricDefinition,
  type RuleDto,
  type SeriesDto,
} from '@citilight-monitor/contracts';
import type { ChartPoint } from './chart';

const compact = new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 });
const decimal = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });

/**
 * How one metric is presented. Up/down metrics are charted as reachability
 * (% of polls in each bucket that got a response).
 */
export interface MetricView {
  definition: MetricDefinition;
  title: string;
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  domain?: [number, number];
  /** Scales stored values to charted values. */
  toChart: (value: number) => number;
}

export function metricView(definition: MetricDefinition): MetricView {
  switch (definition.unit) {
    case 'state':
      return {
        definition,
        title: `${definition.label} (% of polls)`,
        formatValue: (v) => `${decimal.format(v)}%`,
        formatTick: (v) => `${v}%`,
        domain: [0, 100],
        toChart: (v) => v * 100,
      };
    case 'percent':
      return {
        definition,
        title: definition.label,
        formatValue: (v) => formatMetricValue('percent', v),
        formatTick: (v) => `${v}%`,
        domain: [0, 100],
        toChart: (v) => v,
      };
    case 'count':
      return {
        definition,
        title: definition.label,
        formatValue: (v) => formatMetricValue('count', Math.round(v)),
        formatTick: (v) => compact.format(v),
        toChart: (v) => v,
      };
    case 'number':
      return {
        definition,
        title: definition.label,
        formatValue: (v) => formatMetricValue('number', v),
        formatTick: (v) => decimal.format(v),
        toChart: (v) => v,
      };
  }
}

export function chartPoints(series: SeriesDto | undefined, view: MetricView): ChartPoint[] {
  if (!series) return [];
  const points: ChartPoint[] = [];
  for (const point of series.points) {
    const value = point.values[view.definition.key];
    if (!value) continue;
    points.push({
      t: Date.parse(point.t),
      avg: view.toChart(value.avg),
      min: view.toChart(value.min),
      max: view.toChart(value.max),
      polls: point.polls,
    });
  }
  return points;
}

/** Share of polls in the range that got a response, as a percentage (points already scaled to %). */
export function pollWeightedAverage(points: ChartPoint[]): number | undefined {
  const polls = points.reduce((n, p) => n + p.polls, 0);
  return polls ? points.reduce((sum, p) => sum + p.avg * p.polls, 0) / polls : undefined;
}

/** "Alert below 800 · 5 polls in a row" */
export function ruleSummary(rule: RuleDto, metric: MetricDefinition): string {
  const condition = describeCondition('', metric.unit, rule.operator, rule.threshold).trim().replace(/^: /, 'when ');
  const polls = rule.consecutive === 1 ? 'first poll' : `${rule.consecutive} polls in a row`;
  return `Alert ${condition} · ${polls}`;
}

/** Metrics in reading order: application metrics, then server resources, then reachability. */
export function orderMetrics(metrics: MetricDefinition[]): MetricDefinition[] {
  const rank = (m: MetricDefinition) => (m.unit === 'state' ? 2 : m.source === 'app' ? 0 : 1);
  return [...metrics].sort((a, b) => rank(a) - rank(b));
}
